import { Platform } from 'react-native';

function getBaseToken(token: string) {
  const segments = token.split(':');
  return segments[segments.length - 1] ?? token;
}

function isGradientDirectionToken(baseToken: string) {
  return baseToken.startsWith('bg-gradient-to-');
}

function isGradientStopToken(baseToken: string) {
  return (
    baseToken.startsWith('from-') || baseToken.startsWith('via-') || baseToken.startsWith('to-')
  );
}

function isGradientPositionToken(value: string) {
  return /^\d+%$/.test(value);
}

function isTransparentToken(value: string) {
  return value === 'transparent' || value.startsWith('transparent/');
}

function resolveFallbackBackgroundClassName(tokens: string[], fallbackClassName?: string) {
  for (const token of tokens) {
    const baseToken = getBaseToken(token);

    if (!isGradientStopToken(baseToken)) {
      continue;
    }

    const value = baseToken.replace(/^(from|via|to)-/, '');

    if (!value || isGradientPositionToken(value) || isTransparentToken(value)) {
      continue;
    }

    return `bg-${value}`;
  }

  return fallbackClassName ?? '';
}

export function withNativeGradientFallback(className: string, fallbackClassName?: string) {
  // NativeWind does not render CSS gradients on React Native. On app surfaces we
  // strip the gradient tokens and replace them with a stable background color.
  if (Platform.OS === 'web' || !className) {
    return className;
  }

  const tokens = className.split(/\s+/).filter(Boolean);
  const hasGradientTokens = tokens.some((token) => {
    const baseToken = getBaseToken(token);
    return isGradientDirectionToken(baseToken) || isGradientStopToken(baseToken);
  });

  if (!hasGradientTokens) {
    return className;
  }

  const filteredTokens = tokens.filter((token) => {
    const baseToken = getBaseToken(token);
    return !isGradientDirectionToken(baseToken) && !isGradientStopToken(baseToken);
  });

  const hasBackgroundToken = filteredTokens.some((token) => {
    const baseToken = getBaseToken(token);
    return baseToken.startsWith('bg-');
  });

  const fallbackBackgroundClassName = hasBackgroundToken
    ? ''
    : resolveFallbackBackgroundClassName(tokens, fallbackClassName);

  return [...filteredTokens, fallbackBackgroundClassName].filter(Boolean).join(' ');
}
